import { commonMediaQueries } from 'common/constants/mediaQueries'
import { EntityTypeEnum } from 'common/enums/entityTypeEnum'
import { PageLocaleEnum } from 'common/enums/PageLocaleEnum'

import GlobalStyles from 'common/utils/GlobalStyles'
import React, { ReactNode, useEffect } from 'react'
import {
  WebsitePublicPageContentUi,
  WebsitePublicPageHeaderUi,
  WebsitePublicPageLanguageSwitcherContainerUi,
  WebsitePublicPageLayoutContainerUi,
  WebsitePublicPageMainUi,
} from 'src/public-pages/shared/components/layout/ui/website-public-page-ui'
import { getUserData } from 'src/publisher/api/user-data-api'
import { WebsiteRenderDataDto } from 'src/server/modules/website/shared/contracts/website-render-data.dto'
import { CommonWebsiteLanguageSwitcher } from 'src/shared/entities/website-language-switcher'
import { WebsitePublicPageType } from 'src/shared/enums/website-public-page-form-type'
import { ThemeProvider } from 'styled-components'
import { WebsitePublicLogo } from './website-public-logo'

interface PublicPageLayoutProps {
  children: ReactNode
  logoUrl?: string
  isCustomDomain?: boolean
  locale?: PageLocaleEnum
  pageBackgroundColor?: string
  contentBackgroundColor?: string
  pageType: WebsitePublicPageType
  hasWebsite?: boolean
  stylesAndTheme?: WebsiteRenderDataDto
}

const PAGES_WITHOUT_HEADER_WHEN_WEBSITE_IS_MISSING = [
  WebsitePublicPageType.NotFound,
  WebsitePublicPageType.InternalServerError,
]
const PAGES_WITHOUT_CONTENT_WRAPPER = [
  WebsitePublicPageType.NotFound,
  WebsitePublicPageType.InternalServerError,
]
const PAGES_WITHOUT_LANGUAGE_SWITCHER = [
  WebsitePublicPageType.NotFound,
  WebsitePublicPageType.InternalServerError,
]
const pagesNeedingRedirect = [WebsitePublicPageType.Login]

export const PublicPageLayout = ({
  pageBackgroundColor,
  contentBackgroundColor,
  children,
  logoUrl,
  isCustomDomain,
  locale = PageLocaleEnum.ENGLISH,
  pageType,
  hasWebsite = true,
  stylesAndTheme,
}: PublicPageLayoutProps) => {
  const shouldHideHeader =
    !hasWebsite &&
    PAGES_WITHOUT_HEADER_WHEN_WEBSITE_IS_MISSING.includes(pageType)
  const isPageWithoutContentWrapper =
    PAGES_WITHOUT_CONTENT_WRAPPER.includes(pageType)
  const isPageWithoutLanguageSwitcher =
    PAGES_WITHOUT_LANGUAGE_SWITCHER.includes(pageType)
  const shouldShowContentWrapper = !isPageWithoutContentWrapper
  const shouldShowLanguageSwitcher = !isPageWithoutLanguageSwitcher

  useEffect(() => {
    // TODO: add other necessary page types later
    if (!pagesNeedingRedirect.includes(pageType)) {
      return
    }

    const load = async () => {
      const responseUser = await getUserData()
      if (responseUser.data?.homeUrl && responseUser.data?.id) {
        window.location.href = responseUser.data?.homeUrl
      }
    }

    void load()
  }, [pageType])

  return (
    <ThemeProvider
      theme={{
        phone: commonMediaQueries.phone,
        desktop: commonMediaQueries.desktop,
      }}
    >
      <GlobalStyles />
      <WebsitePublicPageLayoutContainerUi
        id="app"
        backgroundColor={pageBackgroundColor}
      >
        {!shouldHideHeader && (
          <WebsitePublicPageHeaderUi>
            <WebsitePublicLogo
              logoUrl={logoUrl}
              isCustomDomain={isCustomDomain}
              alt="Logo"
              style={{ margin: 0 }}
              isHeader
            />
            {shouldShowLanguageSwitcher && (
              <WebsitePublicPageLanguageSwitcherContainerUi>
                <CommonWebsiteLanguageSwitcher
                  id="website-public-page-language-switcher"
                  type={EntityTypeEnum.WebsiteLanguageSwitcher}
                  htmlAttrId="website-public-page-language-switcher"
                  excludedLanguages={[]}
                  currentLanguage={locale}
                  websiteLocales={Object.values(PageLocaleEnum)}
                  websiteDefaultLocale={PageLocaleEnum.ENGLISH}
                  {...stylesAndTheme?.style?.typography?.paragraph}
                  textColor={stylesAndTheme?.theme?.text.paragraphTextColor}
                />
              </WebsitePublicPageLanguageSwitcherContainerUi>
            )}
          </WebsitePublicPageHeaderUi>
        )}
        <WebsitePublicPageMainUi backgroundColor={pageBackgroundColor}>
          {shouldShowContentWrapper ? (
            <WebsitePublicPageContentUi
              backgroundColor={contentBackgroundColor}
            >
              {children}
            </WebsitePublicPageContentUi>
          ) : (
            children
          )}
        </WebsitePublicPageMainUi>
      </WebsitePublicPageLayoutContainerUi>
    </ThemeProvider>
  )
}
